import { useEffect, useState } from 'react';
import { Modal, Button, Textarea, useToast } from '../../../shared/components';
import { getTurnoGeoRepository } from '../../../shared/services/turnoGeo/repositoryFactory';
import { OvertimeCandidate, fmtMinutes } from '../../../shared/services/turnoGeo/types';

const repo = getTurnoGeoRepository();

/**
 * Aviso de horas extras al cerrar la jornada. El empleado es el primer filtro: solo el sabe si el tiempo de mas
 * fue trabajo real o un descuido (por ejemplo, no marco la salida a tiempo). Si dice que si, pasa a la autorizacion del jefe.
 * `refreshKey` cambia cuando hay que volver a preguntarle al servidor (por ejemplo, justo despues de marcar la SALIDA).
 */
export function OvertimePrompt({ refreshKey }: { refreshKey: number }) {
  const toast = useToast();
  const [items, setItems] = useState<OvertimeCandidate[]>([]);
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { repo.getOvertimeCandidates().then(setItems).catch(() => setItems([])); }, [refreshKey]);

  const current = items.find((c) => !dismissed.includes(c.workDate)) ?? null;
  if (!current) return null;

  const next = () => { setAsking(false); setNote(''); setItems((l) => l.filter((c) => c.workDate !== current.workDate)); };
  const decline = async () => {
    setBusy(true);
    try { await repo.declineOvertime(current.workDate); toast.show('Listo: no se registran horas extras para ese día.'); next(); }
    catch (e) { toast.show(e instanceof Error ? e.message : 'No se pudo guardar.', 'error'); }
    finally { setBusy(false); }
  };
  const request = async () => {
    setBusy(true);
    try { await repo.requestOvertime(current.workDate, note.trim()); toast.show('Enviaste tus horas extras a tu jefe para que las autorice.'); next(); }
    catch (e) { toast.show(e instanceof Error ? e.message : 'No se pudo enviar.', 'error'); }
    finally { setBusy(false); }
  };

  return (
    <Modal
      open
      onClose={() => setDismissed((d) => [...d, current.workDate])}
      title="¿Deseas marcar horas extras?"
      footer={
        asking ? (
          <div className="flex gap-2">
            <Button variant="outline" fullWidth onClick={() => setAsking(false)} disabled={busy}>Atrás</Button>
            <Button fullWidth onClick={request} isLoading={busy} disabled={note.trim().length < 5}>Enviar a mi jefe</Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" fullWidth onClick={decline} isLoading={busy}>No, fue un descuido</Button>
            <Button fullWidth onClick={() => setAsking(true)}>Sí, marcar horas extras</Button>
          </div>
        )
      }
    >
      <div className="space-y-3 text-sm text-ink-700">
        <p>
          Tu tiempo de cierre del <b>{current.workDate}</b> es mayor a <b>{current.promptFromMinutes} minutos</b> sobre tu jornada.
        </p>
        <dl className="grid grid-cols-2 gap-2 rounded-xl bg-ink-50 p-3 text-xs">
          <div><dt className="text-ink-400">Entrada – salida</dt><dd className="font-semibold">{current.entry} – {current.exit}</dd></div>
          <div><dt className="text-ink-400">Jornada del día</dt><dd className="font-semibold">{fmtMinutes(current.scheduledMinutes)}</dd></div>
          <div><dt className="text-ink-400">Trabajado (sin almuerzo)</dt><dd className="font-semibold">{fmtMinutes(current.workedMinutes)}</dd></div>
          <div><dt className="text-ink-400">Tiempo adicional</dt><dd className="font-semibold">{fmtMinutes(current.excessMinutes)}</dd></div>
        </dl>
        <p>Se propone reportar <b>{fmtMinutes(current.proposedMinutes)}</b> de horas extras, que tu jefe debe autorizar. Si el tiempo de más fue porque olvidaste marcar a tiempo, responde que no.</p>
        {asking && (
          <Textarea label="¿Qué hiciste en ese tiempo extra?" placeholder="Ej: cierre de inventario solicitado por la coordinadora" value={note} onChange={(e) => setNote(e.target.value)} hint="Mínimo 5 caracteres. Tu jefe lo verá al autorizar." />
        )}
      </div>
    </Modal>
  );
}
